import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  BadgePercent,
  CheckCircle2,
  ExternalLink,
  FileDown,
  Handshake,
  Mail,
  PiggyBank,
  Search,
  Sparkles,
  Star,
  X,
  Zap,
} from 'lucide-react';

type Categoria =
  | 'Reti e associazioni'
  | 'Servizi digitali'
  | 'Formazione ed editoria'
  | 'Veicoli'
  | 'Assicurazioni e finanza'
  | 'Nautica';

interface Convenzione {
  id: string;
  title: string;
  description: string;
  pdfUrl?: string;
  websiteUrl?: string;
  imageName: string;
  discount?: string;
  categoria: Categoria;
}

const convenzioni: Convenzione[] = [
  {
    id: 'consorzio-sermetra',
    title: 'Promo UNASCA: entra nel Consorzio Sermetra con il 50% di sconto!',
    description: 'L\'unica garanzia per affrontare con strumenti up-to-date e supporto costante il tuo futuro professionale di associato UNASCA. Entra nel Consorzio Sermetra con uno sconto del 50% sulla quota di iscrizione! Offerta valida fino al 31 dicembre.',
    websiteUrl: 'https://www.sermetra.it/promo-unasca-consorzio-sermetra/',
    imageName: 'consorzio.png',
    discount: '50%',
    categoria: 'Reti e associazioni',
  },
  {
    id: 'dylog-buffetti',
    title: 'Dylog Buffetti',
    description: 'Offre condizioni vantaggiose sull\'acquisto di prodotti e servizi digitali per autoscuole e studi di consulenza, con sconti specifici per gli associati tramite codice promozionale.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/CONVENZIONE-DYLOG-UNASCA.pdf',
    imageName: 'dylog.png',
    categoria: 'Servizi digitali',
  },
  {
    id: 'egaf',
    title: 'EGAF',
    description: 'Garantisce sconti fino al 30% sui prodotti del catalogo e il 25% sui soli testi editoriali. Per usufruire dello sconto, è necessario contattare EGAF e presentare la ricevuta UNASCA valida.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/Accordo%20UNASCA%20-%20EGAF%20Un%20vantaggio%20esclusivo%20per%20i%20nostri%20associati.pdf',
    imageName: 'egaf.png',
    discount: 'fino al 30%',
    categoria: 'Formazione ed editoria',
  },
  {
    id: 'guido-simplex',
    title: 'Guido Simplex',
    description: 'Una convenzione che offre uno sconto del 50% per l\'adattamento di veicoli multiadattati per le autoscuole, con possibilità di essere messi in contatto con l\'azienda tramite UNASCA.',
    imageName: 'guidosimplex.png',
    discount: '50%',
    categoria: 'Veicoli',
  },
  {
    id: 'unipol',
    title: 'Unipol',
    description: 'Propone diverse combinazioni di massimali e costi per polizze assicurative, a cui gli associati possono accedere tramite la convenzione.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/Unipol-Unasca.pdf',
    imageName: 'unipol.png',
    categoria: 'Assicurazioni e finanza',
  },
  {
    id: 'assigenesi',
    title: 'ASSIGENESI Srls',
    description: 'Offre la possibilità di ottenere attestazioni di capacità finanziaria a tariffe scontate per autoscuole, scuole nautiche e studi di consulenza.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/convenzione_merged.pdf',
    imageName: 'assigenesi.png',
    categoria: 'Assicurazioni e finanza',
  },
  {
    id: 'iostudio',
    title: 'ioStudio',
    description: 'In collaborazione con il Ministero dell\'Istruzione, offre agli studenti della Carta IoStudio coupon per sconti da utilizzare presso le autoscuole UNASCA aderenti all\'iniziativa.',
    imageName: 'IoStudio.png',
    categoria: 'Formazione ed editoria',
  },
  {
    id: 'sailornet-mysa',
    title: 'SAILORNET (MYSA)',
    description: 'Servizi da offrire ai nostri clienti che includono recupero in mare, assistenza tecnica, invio sommozzatori, informazioni nautiche e molto altro con provvigioni fino al 35%.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/ACCORDO-UNASCA-MYSA.pdf',
    imageName: 'sailornet.png',
    discount: 'provvigioni fino al 35%',
    categoria: 'Nautica',
  },
  {
    id: 'owny',
    title: 'OWNY',
    description: 'Introduce per gli associati una piattaforma digitale di pagamento sicuro con escrow agreement, che tutela acquirenti e venditori nelle compravendite di veicoli e offre condizioni agevolate e riconoscimenti economici per le agenzie UNASCA.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/Accordo%20tra%20UNASCA%20e%20Owny%20-%20Nuove%20opportunit%C3%A0%20per%20gli%20associati.pdf',
    imageName: 'owny.png',
    categoria: 'Servizi digitali',
  },
  {
    id: 'eurococ',
    title: 'EUROCOC',
    description: 'Grazie all\'accordo esclusivo tra UNASCA ed EUROCOC gli associati hanno uno sconto nella richiesta dei COC di €15,00.',
    pdfUrl: 'https://www.unasca.it/PDF/servizi/EUROCOC.pdf',
    imageName: 'eurococ.png',
    discount: '€15,00',
    categoria: 'Veicoli',
  },
  {
    id: 'confcommercio',
    title: 'Confcommercio',
    description: 'In collaborazione con CONFCOMMERCIO, gli associati potranno accedere a tutte le convenzioni nazionali, tra cui ITA Airways, Italo Treno, Telepass, i principali istituti di credito e molto altro ancora.',
    websiteUrl: 'https://associati.confcommercio.it/convenzioni-nazionali/',
    imageName: 'confcommercio.png',
    categoria: 'Reti e associazioni',
  },
];

const CATEGORIE: Categoria[] = [
  'Reti e associazioni',
  'Servizi digitali',
  'Formazione ed editoria',
  'Veicoli',
  'Assicurazioni e finanza',
  'Nautica',
];

const fontCorsivo = { fontFamily: "'Caveat', cursive" };

const normalizza = (testo: string) =>
  testo.toLocaleLowerCase('it-IT').normalize('NFD').replace(/[̀-ͯ]/g, '');

// Card a forma di buono sconto: logo, linea tratteggiata con le tacche, contenuto
const BuonoConvenzione: React.FC<{ convenzione: Convenzione }> = ({ convenzione }) => (
  <article className="group relative flex flex-col rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow duration-300 hover:shadow-xl dark:bg-gray-900 dark:ring-white/10 motion-safe:animate-fade-in">
    <div className="flex h-40 items-center justify-center rounded-t-2xl bg-white px-8">
      <img
        src={`/images/convenzioni/${convenzione.imageName}`}
        alt={convenzione.title}
        loading="lazy"
        className="max-h-20 w-auto max-w-full object-contain transition-transform duration-500 motion-safe:group-hover:scale-105"
        onError={(e) => {
          e.currentTarget.src = '/images/logo-unasca.png';
        }}
      />
    </div>

    <div className="relative" aria-hidden="true">
      <div className="mx-5 border-t-2 border-dashed border-gray-200 dark:border-gray-700" />
      <span className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-gray-50 dark:bg-gray-950" />
      <span className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-gray-50 dark:bg-gray-950" />
    </div>

    {convenzione.discount && (
      <div className="absolute right-5 top-[8.6rem] -rotate-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-bold text-white shadow-lg ring-4 ring-white dark:ring-gray-900">
          <BadgePercent className="h-4 w-4" aria-hidden="true" />
          {convenzione.discount}
        </span>
      </div>
    )}

    <div className="flex flex-1 flex-col p-6 pt-5">
      <p className="mb-2 text-xs font-medium text-primary-700 dark:text-primary-400">{convenzione.categoria}</p>
      <h3 className="mb-2 text-lg font-bold leading-snug text-gray-900 dark:text-white">{convenzione.title}</h3>
      <p className="mb-6 flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">{convenzione.description}</p>

      <div className="mt-auto flex flex-col gap-2 sm:flex-row">
        {convenzione.pdfUrl && (
          <a
            href={convenzione.pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
          >
            <FileDown className="h-4 w-4" aria-hidden="true" />
            Scarica PDF
          </a>
        )}
        {convenzione.websiteUrl && (
          <a
            href={convenzione.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:text-white dark:hover:bg-gray-800"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Visita Sito
          </a>
        )}
      </div>
    </div>
  </article>
);

const ConvenzioniPage: React.FC = () => {
  const [categoria, setCategoria] = useState<Categoria | null>(null);
  const [ricerca, setRicerca] = useState('');

  const conteggi = useMemo(() => {
    const c = new Map<Categoria, number>();
    convenzioni.forEach((v) => c.set(v.categoria, (c.get(v.categoria) || 0) + 1));
    return c;
  }, []);

  const filtrate = useMemo(() => {
    const testo = normalizza(ricerca.trim());
    return convenzioni.filter(
      (v) =>
        (!categoria || v.categoria === categoria) &&
        (!testo || normalizza(`${v.title} ${v.description} ${v.categoria}`).includes(testo)),
    );
  }, [categoria, ricerca]);

  const azzera = () => {
    setCategoria(null);
    setRicerca('');
  };

  const pastiglia = (attiva: boolean) =>
    `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950 ${
      attiva
        ? 'bg-primary-700 text-white shadow'
        : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-primary-50 hover:text-primary-800 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Testata */}
      <section className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-y-0 right-0 w-full lg:w-[60%]">
          <img
            src="/images/convenzioni-pagina/stretta-di-mano.webp"
            srcSet="/images/convenzioni-pagina/stretta-di-mano-1280.webp 1280w, /images/convenzioni-pagina/stretta-di-mano.webp 2400w"
            sizes="(min-width: 1024px) 60vw, 100vw"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-primary-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-primary-950 lg:from-[40%] lg:via-primary-950/70 lg:via-[55%] lg:to-transparent" />

        <div className="relative container-custom flex min-h-[32rem] items-center py-20 md:py-24">
          <div className="max-w-xl motion-safe:animate-fade-in">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 ring-1 ring-white/20 backdrop-blur-sm">
              <Handshake className="h-4 w-4 text-primary-300" aria-hidden="true" />
              <span className="text-sm font-semibold text-white">Partnership Esclusive</span>
            </div>

            <h1 className="mb-6 text-4xl md:text-6xl font-bold tracking-tight text-white">
              Convenzioni <span className="font-unasca text-primary-400">UNASCA</span>
            </h1>

            <p className="mb-10 text-base md:text-lg leading-relaxed text-white/80">
              Vantaggi esclusivi per i nostri associati: sconti, servizi dedicati e partnership strategiche
              con i migliori partner del settore automotive e della formazione.
            </p>

            <dl className="grid max-w-lg grid-cols-3 gap-3">
              {[
                { valore: '10+', etichetta: 'Convenzioni Attive' },
                { valore: '50%', etichetta: 'Sconto Massimo' },
                { valore: '2000+', etichetta: 'Associati' },
              ].map(({ valore, etichetta }) => (
                <div key={etichetta} className="flex flex-col-reverse rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur-sm">
                  <dt className="text-xs md:text-sm leading-tight text-white/75">{etichetta}</dt>
                  <dd className="mb-1 text-2xl md:text-3xl font-bold text-white">{valore}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-10 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
          <span className="block text-4xl leading-tight drop-shadow-lg" style={fontCorsivo}>
            Vantaggi che fanno<br />la differenza
          </span>
          <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-56 text-primary-400" fill="none">
            <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </div>
      </section>

      {/* In evidenza */}
      <section className="py-16 md:py-20">
        <div className="container-custom">
          <p className="mb-6 text-xs font-semibold uppercase tracking-wider text-primary-700 dark:text-primary-400">In evidenza</p>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Consorzio Sermetra */}
            <div className="relative flex flex-col overflow-hidden rounded-3xl bg-gradient-to-br from-primary-800 to-primary-950 p-7 text-white shadow-2xl md:p-9">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-500/20 blur-3xl" aria-hidden="true" />
              <div className="relative mb-6 flex items-start justify-between gap-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold ring-1 ring-white/25">
                  <Star className="h-3.5 w-3.5 fill-current text-amber-300" aria-hidden="true" />
                  ESCLUSIVO
                </span>
                <div className="flex-shrink-0 rounded-xl bg-white p-3 shadow-lg">
                  <img src="/images/convenzioni/consorzio.png" alt="Consorzio Sermetra" className="h-10 w-auto object-contain" />
                </div>
              </div>
              <h2 className="relative mb-2 text-2xl md:text-3xl font-bold tracking-tight">Promo UNASCA: Consorzio Sermetra</h2>
              <p className="relative mb-5 text-sm font-semibold text-primary-300">50% di sconto - Offerta fino al 31 dicembre</p>
              <p className="relative mb-8 flex-1 text-base leading-relaxed text-white/85">
                L'unica garanzia per affrontare con <strong className="text-white">strumenti up-to-date e supporto costante</strong> il tuo futuro professionale di associato UNASCA.
              </p>
              <a
                href="https://www.sermetra.it/promo-unasca-consorzio-sermetra/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center gap-2 self-start rounded-full bg-white px-6 py-3 font-semibold text-primary-800 shadow-lg transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-900"
              >
                Scopri l'Offerta
                <ArrowRight className="h-5 w-5 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
              </a>
            </div>

            {/* Confcommercio */}
            <div className="relative flex flex-col overflow-hidden rounded-3xl bg-white p-7 shadow-xl ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10 md:p-9">
              <div className="relative mb-6 flex items-start justify-between gap-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-bold text-primary-800 ring-1 ring-primary-200 dark:bg-primary-900/40 dark:text-primary-200 dark:ring-primary-800">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  NOVITÀ
                </span>
                <div className="flex-shrink-0 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5">
                  <img src="/images/convenzioni/confcommercio.png" alt="Confcommercio" className="h-10 w-auto object-contain" />
                </div>
              </div>
              <h2 className="mb-2 text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                UNASCA <span className="text-primary-600 dark:text-primary-400">×</span> CONFCOMMERCIO
              </h2>
              <p className="mb-5 text-sm font-semibold text-primary-700 dark:text-primary-400">Partnership Strategica 2025</p>
              <p className="mb-8 flex-1 text-base leading-relaxed text-gray-700 dark:text-gray-300">
                Dall'unione di queste due realtà, <strong className="text-gray-900 dark:text-white">gli associati UNASCA riceveranno automaticamente anche la tessera di CONFCOMMERCIO</strong>.
              </p>
              <ul className="flex flex-wrap gap-2">
                {['Tessera Automatica', 'Doppi Vantaggi', 'Convenzioni Nazionali'].map((voce) => (
                  <li key={voce} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                    <CheckCircle2 className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
                    {voce}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Elenco convenzioni */}
      <section className="pb-20 md:pb-24">
        <div className="container-custom">
          <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="mb-2 text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                Convenzioni Principali
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
                Scopri tutti i vantaggi riservati agli associati UNASCA
              </p>
            </div>
            <div className="relative w-full lg:w-80">
              <label htmlFor="cerca-convenzione" className="sr-only">Cerca una convenzione</label>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                id="cerca-convenzione"
                type="search"
                value={ricerca}
                onChange={(e) => setRicerca(e.target.value)}
                placeholder="Cerca una convenzione"
                className="input rounded-full pl-10"
              />
            </div>
          </div>

          <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label="Filtra per categoria">
            <button type="button" aria-pressed={!categoria} onClick={() => setCategoria(null)} className={pastiglia(!categoria)}>
              Tutte
              <span className="text-xs opacity-70">{convenzioni.length}</span>
            </button>
            {CATEGORIE.map((c) => (
              <button key={c} type="button" aria-pressed={categoria === c} onClick={() => setCategoria(categoria === c ? null : c)} className={pastiglia(categoria === c)}>
                {c}
                <span className="text-xs opacity-70">{conteggi.get(c) || 0}</span>
              </button>
            ))}
          </div>

          <p className="sr-only" aria-live="polite">
            {filtrate.length === 1 ? '1 convenzione' : `${filtrate.length} convenzioni`}
          </p>

          {filtrate.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {filtrate.map((convenzione) => (
                <BuonoConvenzione key={`${convenzione.id}-${categoria || 'tutte'}-${ricerca}`} convenzione={convenzione} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
              <p className="mb-4 text-base text-gray-700 dark:text-gray-300">Nessuna convenzione corrisponde alla ricerca.</p>
              <button type="button" onClick={azzera} className="btn btn-secondary">
                <X className="mr-2 h-4 w-4" aria-hidden="true" />
                Mostra tutte le convenzioni
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-primary-950 py-20 md:py-24">
        <img
          src="/images/convenzioni-pagina/strada-negozi.webp"
          srcSet="/images/convenzioni-pagina/strada-negozi-960.webp 960w, /images/convenzioni-pagina/strada-negozi.webp 1920w"
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60" />

        <div className="container-custom relative">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="mb-6 text-4xl md:text-5xl font-bold leading-tight tracking-tight text-white">
              Vuoi accedere a tutte<br />queste <span className="text-primary-300">convenzioni</span>?
            </h2>

            <p className="mx-auto mb-12 max-w-2xl text-lg md:text-xl leading-relaxed text-white/85">
              Diventa associato UNASCA e approfitta di tutti i vantaggi riservati ai nostri membri
            </p>

            <ul className="mx-auto mb-12 grid max-w-3xl grid-cols-1 gap-4 md:grid-cols-3">
              {[
                { titolo: 'Risparmio Garantito', testo: 'Sconti fino al 50%', Icona: PiggyBank },
                { titolo: 'Partner Affidabili', testo: 'Leader del settore', Icona: Handshake },
                { titolo: 'Accesso Immediato', testo: 'Attivazione rapida', Icona: Zap },
              ].map(({ titolo, testo, Icona }) => (
                <li key={titolo} className="rounded-2xl bg-white/10 p-6 ring-1 ring-white/15 backdrop-blur-sm">
                  <Icona className="mx-auto mb-3 h-7 w-7 text-primary-300" aria-hidden="true" />
                  <div className="mb-1 font-semibold text-white">{titolo}</div>
                  <div className="text-sm text-white/75">{testo}</div>
                </li>
              ))}
            </ul>

            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/diventa-socio" className="btn btn-primary btn-large group focus:ring-offset-primary-950">
                Diventa Associato
                <ArrowRight className="ml-2 h-5 w-5 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="btn btn-large border border-white/40 bg-transparent text-white hover:bg-white/10 focus:ring-white focus:ring-offset-primary-950"
              >
                <Mail className="mr-2 h-5 w-5" aria-hidden="true" />
                Contattaci
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConvenzioniPage;
